import { useCallback, useEffect, useState } from "react";
import { getClusteringMap } from "../api";
import type { ClusteringMapResponse, Granularity } from "../types";

interface UseClusteringMapResult {
  response: ClusteringMapResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch the clustering map for the given granularity and `asOf` window-end,
 * refetching whenever either changes. Mirrors `insights/hooks/useChartAggregate`.
 *
 * `asOf` (null = the live "now" view) drives the history time-slider. The slider
 * commits a new value only on release, so dragging is a single fetch — no extra
 * debounce here. `response` is kept across refetches so changing the window does
 * not flash the page back to a skeleton — `loading` drives an inline indicator.
 */
export function useClusteringMap(
  granularity: Granularity,
  asOf: string | null,
): UseClusteringMapResult {
  const [response, setResponse] = useState<ClusteringMapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResponse(await getClusteringMap(granularity, asOf ?? undefined));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load failure clusters.";
      setError(message);
      console.error("Failed to load clustering map:", err);
    } finally {
      setLoading(false);
    }
  }, [granularity, asOf]);

  useEffect(() => {
    fetchMap();
  }, [fetchMap]);

  return { response, loading, error, refetch: fetchMap };
}
